/**
 * PrintAuditPopover (WCO-2618)
 *
 * A per-row (i) info button that, on hover, shows the label's full print/reprint/supersede history —
 * so a supervisor understands why "Print Labels" is disabled (already printed by X on Y at weight Z).
 *
 * Data-source-agnostic: it just renders whatever `history` array it's given (currently FP-NI / RM-NI
 * lot-label history from ics.hte_lot_label_print_history via the daily-transfer-out response; a
 * by-products source can reuse this component later). Renders nothing when there is no history.
 *
 * Positioning/portal/viewport-flip is delegated to HoverTooltip (escapes the modal's overflow clip).
 */
import React from 'react';
import { Info } from 'lucide-react';
import HoverTooltip from './HoverTooltip';
import { useLanguage } from '../../../i18n/LanguageContext';
import { formatDateTimeLocal } from '../../../utils/dateUtils';
import { PrintHistoryEntry } from '../../../services/dailyTransferOutService';

interface PrintAuditPopoverProps {
  history?: PrintHistoryEntry[];
}

// status → chip styling + i18n key. 'active' = current label, 'discarded' = superseded by a reprint.
const STATUS_CHIP: Record<PrintHistoryEntry['status'], { cls: string; key: string }> = {
  active: { cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300', key: 'printAudit.statusActive' },
  discarded: { cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300', key: 'printAudit.statusSuperseded' },
  voided: { cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', key: 'printAudit.statusVoided' },
};

const PrintAuditPopover: React.FC<PrintAuditPopoverProps> = ({ history }) => {
  const { t } = useLanguage();

  // The (i) is always shown as a consistent affordance (WCO-2618). When there's no history yet it
  // shows a "No labels printed yet" empty state, so a never-printed row still explains itself.
  const entries = history ?? [];
  const hasHistory = entries.length > 0;

  // WCO-2618: color the (i) blue when there IS print history (at-a-glance "something was printed"),
  // and leave it neutral gray when nothing has been printed yet.
  const iconClass = hasHistory
    ? 'text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 focus:outline-none'
    : 'text-gray-400 hover:text-blue-500 dark:text-gray-500 dark:hover:text-blue-400 focus:outline-none';

  const content = (
    <div className="text-left">
      <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
        {t('printAudit.title')}
      </div>
      {entries.length === 0 ? (
        <div className="text-xs text-gray-500 dark:text-gray-400">{t('printAudit.empty')}</div>
      ) : (
      <div className="space-y-1.5">
        {entries.map((h, idx) => {
          const chip = STATUS_CHIP[h.status] || STATUS_CHIP.active;
          return (
            <div key={`${h.print_number}-${h.barcode ?? idx}`} className="text-xs border-t border-gray-100 dark:border-gray-700 pt-1.5 first:border-t-0 first:pt-0">
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-gray-800 dark:text-gray-100">#{h.print_number}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${chip.cls}`}>
                  {t(chip.key)}
                </span>
                {h.printed_weight != null && (
                  <span className="text-gray-600 dark:text-gray-300">{h.printed_weight.toFixed(2)} lb</span>
                )}
              </div>
              {(h.printed_by || h.printed_at) && (
                <div className="text-gray-600 dark:text-gray-300">
                  {t('printAudit.by')} {h.printed_by || '—'} · {formatDateTimeLocal(h.printed_at)}
                </div>
              )}
              {h.status === 'discarded' && h.discard_reason && (
                <div className="text-[11px] text-gray-400 dark:text-gray-500 italic">{h.discard_reason}</div>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );

  return (
    <HoverTooltip
      content={content}
      tapToOpen
      className="inline-flex ml-1 align-middle"
      panelClassName="z-50 w-64 max-w-[16rem] p-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg"
    >
      <button
        type="button"
        aria-label={t('printAudit.title')}
        className={iconClass}
      >
        <Info className="w-3.5 h-3.5" />
      </button>
    </HoverTooltip>
  );
};

export default PrintAuditPopover;
